import React from 'react';
import { Segment, List, Label, Item, Image, Icon, Popup } from 'semantic-ui-react';
import { Link } from 'react-router-dom';
import { observer } from 'mobx-react-lite';
import { Game } from '../../../app/models/Game';
import { useTranslation } from 'react-i18next';
import { useStore } from '../../../app/stores/store';
import UserScoreButton from '../../userRatings/UserScoreButton';
import InviteFriendsToGame from '../../friends/InviteFriendsToGame';

interface Props {
    game: Game;
}

function SportLevelBadge({ level, t }: { level: number | null | undefined; t: Function }) {
    if (level == null) {
        return (
            <span style={{ fontSize: '0.72em', color: '#bbb' }}>
                {t('favoriteSports.notAssessed')}
            </span>
        );
    }
    return (
        <Popup
            trigger={
                <span style={{ display: 'flex', alignItems: 'center', gap: 2, cursor: 'default' }}>
                    {[1, 2, 3, 4, 5].map(star => (
                        <Icon
                            key={star}
                            name={star <= level ? 'star' : 'star outline'}
                            style={{
                                color: star <= level ? '#f59e0b' : '#d1d5db',
                                fontSize: '0.85em',
                                margin: 0
                            }}
                        />
                    ))}
                    <span style={{ fontSize: '0.75em', color: '#888', marginLeft: 2 }}>
                        {level}/5
                    </span>
                </span>
            }
            content={t('favoriteSports.selfAssessed')}
            size="mini"
            inverted
        />
    );
}

export default observer(function GameDetailedSidebar({
    game: { gameParticipants, host, sportsID, id, isGoing }
}: Props) {
    const { t } = useTranslation();
    const { messageStore } = useStore();

    if (!gameParticipants) return null;

    return (
        <>
            <Segment
                textAlign='center'
                style={{ border: 'none' }}
                attached='top'
                secondary
                inverted
                color='teal'
            >
                {t('gameDetailedSidebar.participantsCount', {
                    count: gameParticipants.length,
                    people: gameParticipants.length === 1
                        ? t('gameDetailedSidebar.person')
                        : t('gameDetailedSidebar.people')
                })}
                &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
                {isGoing && <InviteFriendsToGame gameId={id} />}
            </Segment>

            <Segment attached>
                <List relaxed divided>
                    {gameParticipants.map(participant => {
                     
                        return (
                            <Item style={{ position: 'relative' }} key={participant.userID}>
                                {participant.userID === host?.userID && (
                                    <Label
                                        style={{ position: 'absolute' }}
                                        color='orange'
                                        ribbon='right'
                                    >
                                        {t('gameDetailedSidebar.hostLabel')}
                                    </Label>
                                )}

                                <div style={{ position: 'relative', display: 'inline-block' }}>
                                    <Image
                                        size='tiny'
                                        src={participant.image && participant.image !== ''
                                            ? participant.image
                                            : '/assets/user.png'}
                                    />
                                    <div
                                        style={{
                                            position: 'absolute',
                                            bottom: 8,
                                            right: 8,
                                            width: 14,
                                            height: 14,
                                            borderRadius: '50%',
                                            backgroundColor: messageStore.isUserOnline(participant.userID)
                                                ? '#21ba45'
                                                : '#9e9e9e',
                                            border: '2px solid white',
                                            boxShadow: '0 0 0 1px rgba(0,0,0,0.1)',
                                            zIndex: 1
                                        }}
                                        title={messageStore.isUserOnline(participant.userID)
                                            ? 'Online' : 'Offline'}
                                    />
                                </div>

                                <Item.Content verticalAlign='middle'>
                                    <div style={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        gap: 10
                                    }}>
                                        <div style={{ flex: 1, minWidth: 0 }}>
                                            <Item.Header as='h3'>
                                                <Link to={`/profiles/${participant.userID}`}>
                                                    {participant.fullName}
                                                </Link>
                                            </Item.Header>
                                            {participant.following && (
                                                <Item.Extra style={{ color: 'orange' }}>
                                                    {t('gameDetailedSidebar.followingLabel')}
                                                </Item.Extra>
                                            )}
                                            
                                        </div>

                                        <div style={{ flexShrink: 0 }}>
                                            <UserScoreButton
                                                userId={participant.userID}
                                                sportId={sportsID}
                                                buttonText={t('gameDetailedSidebar.viewStats') || 'Stats'}
                                                size='medium'
                                            />
                                        </div>
                                    </div>
                                </Item.Content>
                            </Item>
                        );
                    })}
                </List>
            </Segment>
        </>
    );
});